import { useCallback, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRenderStore } from '../model/render-form.store';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';
import type { RenderCreateRequest } from '@/types/render';
import { ClassTypes } from '@/shared/constants/class-types';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import { useProjectFeatures } from '@/features/subscription';

const logger = createLogger('RenderSubmit');

interface UseRenderSubmitOptions {
  isDerivative?: boolean;
  onSubmit: (data: RenderCreateRequest) => Promise<void>;
  conceptAngleId?: string;
  /// Project to flush goals for before posting the render. The server
  /// snapshots `project.goals` into the new render at create time, so a
  /// pending PATCH must land first or the render captures stale goals.
  projectId?: string;
}

// Dedicated hook for render submission
export function useRenderSubmit({
  isDerivative = false,
  onSubmit,
  conceptAngleId,
  projectId,
}: UseRenderSubmitOptions) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  // Multi-product return — the manual engine knobs split by tier (mirrors the
  // server gates in ConceptRender::buildRenderDto and the settings sheet):
  //  - EFFORT is a BASE control for token generations — ALWAYS sent, for every
  //    user (they pay tokens for a pricier tier). HIGH still hits the server's
  //    high_effort gate, which returns a typed paywall the caller routes.
  //  - MODEL family (Aurora/Atlas): ATLAS is an ADVANCED control per the 2026-07
  //    "non-photo styles + Atlas are advanced controls" gating — the server
  //    (ConceptRender::buildRenderDto) now gates it on `advanced_render_controls`,
  //    NOT `model_selection`, and Apple/Android gate the payload field on the same
  //    flag. Sent only when the building can use advanced controls; otherwise
  //    omitted so the server renders on the default family (Aurora) instead of
  //    paywalling a stale ATLAS draft. One flag now drives the UI lock AND the
  //    payload — no skew.
  //  - HI-RES / EXTENDED THINKING stay PAID — sent only when the building
  //    carries that specific feature. Omitting the keys lets the server keep its
  //    default (off) instead of leaking the form defaults into every render.
  // Reads the same per-building flags the settings sheet gates the rows on.
  const features = useProjectFeatures(projectId);
  const hasHiresRender = features.has_hires_render;
  const hasExtendedThinking = features.has_extended_thinking;
  // OUTPUT STYLE (non-photorealistic presets + custom "from reference" style) is
  // an ADVANCED control per the 2026-07 "non-photo styles + Atlas are advanced
  // controls" gating decision — sent only when the building can use advanced
  // controls. Otherwise the output-style fields are neutralized so a stale
  // locked draft can't apply a non-photo style; the server renders photorealistic
  // (its default). Mirrors the server gate. Photorealistic == both fields null.
  const advancedUsable = features.has_advanced_render_controls;
  const generateNextSeed = useRenderStore((state) => state.generateNextSeed);
  const resetChangesToDefaults = useRenderStore(
    (state) => state.resetChangesToDefaults
  );
  const tCommon = useTranslations('common');

  // Plan/balance/queue gates live in `useRenderPreflight` and run from the
  // Generate-button click handler (Apple parity with RenderSettingsSheet:
  // preflight in the toolbar handler, server 422s caught downstream). Don't
  // re-gate here — a silent return at this layer is what made the drawer
  // button look dead instead of routing to the paywall.

  const handleSubmit = useCallback(
    async () => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);

        // Flush any in-flight Project goals edit before POSTing /renders.
        // Server snapshots project.goals into the new render at create time,
        // so a pending PATCH must land first. No-op when nothing is pending.
        if (projectId) {
          const saved = await goalsAutosaveCoordinator.flushAndAwait(projectId);
          if (!saved) {
            toast.error(tCommon('error'), {
              description: tCommon('errors.generic'),
            });
            setIsSubmitting(false);
            return;
          }
        }

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set timeout to re-enable button after 10 seconds (only for derivatives)
        if (isDerivative) {
          timeoutRef.current = setTimeout(() => {
            setIsSubmitting(false);
            toast.warning(tCommon('warning'), {
              description: tCommon('errors.generic'),
            });
          }, 10000);
        }

        // Generate next seed based on mode (Refine keeps it; Explore re-rolls).
        generateNextSeed();

        // Get updated formData after seed generation
        const currentFormData = useRenderStore.getState().formData;

        logger.info('Current formData:', {
          isDerivative,
          formData: currentFormData,
          output_style_id: currentFormData.output_style_id,
          prompts: currentFormData.prompts,
          environment_setting: currentFormData.environment_setting,
        });

        // Prepare data for request - remove UI-only fields
        const { ...requestData } = currentFormData;

        // Ensure null values stay null (don't get converted to defaults):
        // [] and null both mean "no selection / auto" — normalize to null.
        if (
          requestData.style_ids === null ||
          (Array.isArray(requestData.style_ids) &&
            requestData.style_ids.length === 0)
        ) {
          requestData.style_ids = null;
        }
        if (
          requestData.material_ids === null ||
          (Array.isArray(requestData.material_ids) &&
            requestData.material_ids.length === 0)
        ) {
          requestData.material_ids = null;
        }
        if (
          requestData.colors === null ||
          (Array.isArray(requestData.colors) &&
            requestData.colors.length === 0)
        ) {
          requestData.colors = null;
        }
        if (
          requestData.brand_selections === null ||
          (Array.isArray(requestData.brand_selections) &&
            requestData.brand_selections.length === 0)
        ) {
          requestData.brand_selections = null;
        }

        // ONE payload builder for main AND derivative renders (Apple/Android
        // parity — both use a single builder). project_goals and
        // construction_region are intentionally omitted: the server snapshots
        // them from Project at render-create time. Derivatives MUST send the
        // reference fields and auto-flags too — the server defaults missing
        // auto-flags to TRUE, so omitting them silently auto-injected project
        // references into derivative renders that Apple/Android suppress, and
        // dropped Selective/output reference picks.
        const renderData: RenderCreateRequest = {
          data: {
            type: ClassTypes.RENDER,
            attributes: {
              version: requestData.version,
              seed: requestData.seed,
              style_ids: requestData.style_ids,
              colors: requestData.colors,
              // Real manufacturer products (Brands) — a separate axis from
              // colors, see App\Services\Brand\BrandSelectionResolver.
              brand_selections: requestData.brand_selections,
              material_ids: requestData.material_ids,
              prompts: requestData.prompts,
              controls: requestData.controls,
              // Output style is advanced-gated — neutralize to photorealistic
              // (null) when the building can't use advanced controls.
              output_style_id: advancedUsable
                ? requestData.output_style_id
                : null,
              environment_setting: requestData.environment_setting,
              // Project references by category
              style_project_reference_ids:
                requestData.style_project_reference_ids,
              material_project_reference_ids:
                requestData.material_project_reference_ids,
              color_project_reference_ids:
                requestData.color_project_reference_ids,
              output_project_reference_ids: advancedUsable
                ? requestData.output_project_reference_ids
                : null,
              // Auto-use project references flags
              use_auto_style_refs: requestData.use_auto_style_refs,
              use_auto_material_refs: requestData.use_auto_material_refs,
              use_auto_color_refs: requestData.use_auto_color_refs,
              // Refine: when 'refine', server uses parent_render_id's image as
              // the pipeline input and switches to the refinement template.
              mode: requestData.mode,
              parent_render_id:
                requestData.mode === 'refine'
                  ? requestData.parent_render_id
                  : null,
              // Effort is a BASE control for token generations — ALWAYS sent.
              // STANDARD/BRAINSTORM flow through; HIGH hits the server's
              // high_effort gate (typed paywall) if the building lacks it.
              render_effort: requestData.render_effort,
              // Model family (Aurora/Atlas) — ATLAS is advanced-gated (server
              // gates on advanced_render_controls); sent only when the building
              // can use advanced controls, so a stale ATLAS draft on a
              // free/token building can't trip the paywall — the server falls
              // back to the default family (Aurora).
              ...(advancedUsable
                ? { render_model_family: requestData.render_model_family }
                : {}),
              // Hi-Res toggle (~2K native) — PAID, sent only when the building
              // carries hires_render. Server rejects a bypassed
              // payload with PACK_REQUIRED otherwise.
              ...(hasHiresRender ? { is_hires: requestData.is_hires } : {}),
              // Extended thinking (stronger reasoning model for the design
              // prompt) — PAID, sent only when the building carries
              // extended_thinking. Server rejects a bypassed
              // payload with PACK_REQUIRED otherwise.
              ...(hasExtendedThinking
                ? { is_extended_thinking: requestData.is_extended_thinking }
                : {}),
            },
          },
        };

        logger.info('Final renderData to send:', {
          isDerivative,
          renderData,
          output_style_id: renderData.data.attributes.output_style_id,
          prompts: renderData.data.attributes.prompts,
          environment_setting: renderData.data.attributes.environment_setting,
        });

        await onSubmit(renderData);

        // Note: resetChangesToDefaults is now called by RenderSettingsDrawer
        // after it updates defaultValues with the submitted settings.
        // This ensures formData is reset to the NEW settings, not the old ones.
      } finally {
        // Clear timeout when request completes
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      generateNextSeed,
      resetChangesToDefaults,
      isDerivative,
      conceptAngleId,
      projectId,
      onSubmit,
      tCommon,
      hasHiresRender,
      hasExtendedThinking,
      advancedUsable,
    ]
  );

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return {
    submitRender: handleSubmit,
    isSubmitting,
    cleanup,
  };
}
